import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Upload,
  FileText,
  Download,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  AlertCircle,
  Plus,
  ArrowRight,
  Star,
  Users,
  Target,
  TrendingUp,
  Briefcase,
  MapPin,
  Calendar,
  Mail,
  Phone,
  Globe,
  Linkedin,
  Github,
  Award,
  BookOpen,
  Zap,
  Shield,
  Settings,
  BarChart3,
  MessageSquare,
} from "lucide-react";
const processSteps = [
  {
    number: "01",
    title: "Submit Your Job",
    description:
      "Fill out our detailed job submission form with your requirements and preferences.",
  },
  {
    number: "02",
    title: "Expert Matching",
    description:
      "Our recruitment specialists handpick candidates that match your specific criteria.",
  },
  {
    number: "03",
    title: "Talent Acquisition",
    description:
      "Receive a curated list of pre-vetted professionals ready to fill your position.",
  },
];

const CVUploadPage = () => {
  const [activeTab, setActiveTab] = useState("upload");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [profile, setProfile] = useState({
    name: "John Doe",
    email: "john.doe@example.com",
    phone: "+1 (555) 123-4567",
    location: "San Francisco, CA",
    title: "Senior Software Developer",
    experience: "8 years",
    education: "Bachelor of Computer Science",
    skills: ["JavaScript", "React", "Node.js", "Python", "AWS", "Docker"],
    summary:
      "Experienced software developer with expertise in full-stack development and cloud technologies.",
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const newFiles = Array.from(files).map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      status: "uploading",
      progress: 0,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setIsUploading(true);

    // Upload each file to the API
    Array.from(files).forEach((file, index) => {
      const formData = new FormData();
      formData.append("resume", file);
      fetch("https://techmindinfosys.com/api/upload_resume.php", {
        method: "POST",
        body: formData,
      })
        .then((res) => {
          if (!res.ok) throw new Error("Upload failed");
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.name === file.name && f.status === "uploading"
                ? { ...f, status: "completed", progress: 100 }
                : f
            )
          );
        })
        .catch(() => {
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.name === file.name && f.status === "uploading"
                ? { ...f, status: "error", progress: 0 }
                : f
            )
          );
        })
        .finally(() => {
          setIsUploading(false);
        });
    });

    // Simulate upload progress for UI feedback
    newFiles.forEach((file, index) => {
      const interval = setInterval(() => {
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === file.id && f.status === "uploading"
              ? { ...f, progress: Math.min(f.progress + 10, 100) }
              : f
          )
        );
      }, 200);

      setTimeout(() => {
        clearInterval(interval);
      }, 2000);
    });
  };

  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const downloadFile = (file) => {
    // Simulate download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob(["CV Content"]));
    link.download = file.name;
    link.click();
  };

  const templates = [
    {
      name: "Professional",
      description: "Clean and professional design suitable for corporate roles",
      preview: "/api/placeholder/300/400",
      downloads: 1250,
      rating: 4.8,
    },
    {
      name: "Creative",
      description: "Modern design perfect for creative and design roles",
      preview: "/api/placeholder/300/400",
      downloads: 890,
      rating: 4.6,
    },
    {
      name: "Minimalist",
      description: "Simple and elegant design for any industry",
      preview: "/api/placeholder/300/400",
      downloads: 2100,
      rating: 4.9,
    },
    {
      name: "Executive",
      description: "Sophisticated design for senior and executive positions",
      preview: "/api/placeholder/300/400",
      downloads: 650,
      rating: 4.7,
    },
  ];

  const tools = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Resume Builder",
      description: "Create professional resumes with our easy-to-use builder",
      link: "#",
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "ATS Optimizer",
      description: "Optimize your resume for Applicant Tracking Systems",
      link: "#",
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Skills Assessment",
      description: "Test and validate your technical skills",
      link: "#",
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Interview Prep",
      description: "Practice common interview questions and scenarios",
      link: "#",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1A193B] pt-20 text-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Submit Your CV
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              Share your profile with our hiring team_
            </p>
          </motion.div>
        </div>
      </section>

      <div className="pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Join Our Talent Pool
          </h1>
          <p className="text-lg md:text-xl max-w-5xl mx-auto mb-8">
            Even if you don't see a specific job opening that matches your
            profile, we'd love to hear from you. Upload your CV to our talent
            pool, and we'll contact you when a suitable position becomes
            available.
          </p>
        </motion.div>
      </div>
      {/* Form Section */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Upload Your CV
            </h2>
            <form
              className="space-y-6"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const formData = new FormData();
                formData.append("applicant_name", form.applicant_name.value);
                formData.append("email", form.email.value);
                if (form.phone.value)
                  formData.append("phone", form.phone.value);
                if (form.skills.value)
                  formData.append("skills", form.skills.value);
                if (form.experience_years.value)
                  formData.append(
                    "experience_years",
                    form.experience_years.value
                  );
                if (form.education.value)
                  formData.append("education", form.education.value);
                if (form.cv_file.files[0])
                  formData.append("cv_file", form.cv_file.files[0]);
                setIsUploading(true);
                setUploadProgress(0);
                try {
                  const res = await fetch(
                    "https://techmindinfosys.com/server/api/cv-documents.php",
                    {
                      method: "POST",
                      body: formData,
                    }
                  );
                  if (!res.ok) throw new Error("Upload failed");
                  setIsUploading(false);

                  alert("CV uploaded successfully!");
                  // ✅ Reset form fields
                  form.reset();
                } catch (err) {
                  setIsUploading(false);
                  alert("Upload failed. Please try again.");
                }
              }}
              encType="multipart/form-data"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="applicant_name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience (optional)
                  </label>
                  <input
                    type="text"
                    name="experience_years"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Years of experience"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Skills (optional)
                </label>
                <input
                  type="text"
                  name="skills"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. JavaScript, React, Node.js, CSS (separate with commas)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education (optional)
                </label>
                <textarea
                  name="education"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your educational background"
                ></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CV Document *{" "}
                  <span className="text-xs text-gray-500">
                    (PDF, DOC, DOCX - max 5MB)
                  </span>
                </label>
                <input
                  type="file"
                  name="cv_file"
                  accept=".pdf,.doc,.docx"
                  required
                  className="w-full"
                />
              </div>
              <button
                type="submit"
                disabled={isUploading}
                className="w-full inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                {isUploading ? "Uploading..." : "Upload CV"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="bg-slate-900 py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="bg-slate-800 border border-slate-700 rounded-lg p-8 hover:border-slate-600 transition-colors"
              >
                <div className="text-4xl font-bold text-blue-400 mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  {step.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12 p-8 bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-xl text-center max-w-4xl mx-auto border border-blue-800/50"
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Have Questions?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-4xl mx-auto leading-relaxed">
              Our recruitment team is ready to assist you with any questions
              about the application process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <button
                  // onClick={handleContactUs}
                  className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Contact Us
                </button>
              </Link>
              <Link to="/moderator-login">
                <button
                  // onClick={handleModeratorAccess}
                  className="px-8 py-3 bg-slate-600 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
                >
                  Moderator Access
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CVUploadPage;
